<script setup lang="ts">
import type { Cart } from '@/js/types/cart.ts';
import type { Client } from '@/js/types/client.ts';
import type { DadataParams, Suggestion } from '@/js/types/dadata.ts';
import type { Route } from '@/js/types/map.ts';
import type { DeliveryInfo, DeliveryPricing, LiftPricing, OrderRequest } from '@/js/types/order.ts';
import { getGeocoding } from '@/js/api/dadata.ts';
import { createOrder } from '@/js/api/order.ts';
import mkad from '@/js/data/coords/mkad-out.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';
import { ERROR_REQUEST_TEXT, showErrorNotify } from '@/js/notification.ts';
import { getPhonePlaceholder } from '@/js/phone-maska.ts';
import {
  convertCoordinates,
  findClosestPointOnMkadPolygon,
  isPointInMkadPolygon,
  isPointInMosoblPolygon,
} from '@/js/turf.ts';
import { classname, formatPrice } from '@/js/utils';
import BaseButton from '@/js/vue/base/base-button.vue';
import BaseCheckbox from '@/js/vue/base/base-checkbox.vue';
import AppHint from '@/js/vue/base/base-hint.vue';
import BaseIcon from '@/js/vue/base/base-icon.vue';
import BaseInputDate from '@/js/vue/base/base-input-date.vue';
import BaseInputRadio from '@/js/vue/base/base-input-radio.vue';
import BaseInput from '@/js/vue/base/base-input.vue';
import AppLoader from '@/js/vue/base/base-loader.vue';
import BaseSelect from '@/js/vue/base/base-select.vue';
import BaseInputTel from '@/js/vue/base/base-tel.vue';
import BaseTextarea from '@/js/vue/base/base-textarea.vue';
import AppAdditionalService from '@/js/vue/checkout/additional-service.vue';
import AddressSuggestions from '@/js/vue/checkout/address-suggestions.vue';
import AppStatus from '@/js/vue/checkout/app-status.vue';
import AppCallManager from '@/js/vue/checkout/call-manager.vue';
import {
  cleanOrderFields,
  getCallTimes,
  getDadataCityParams,
  getDeliveryMethods,
  getDeliveryTimes,
  getFloors,
  getPaymentMethods,
  MOSCOW_REGION,
  MOSKOVSKAYA_REGION,
  suggestToCity,
} from '@/js/vue/checkout/data.ts';
import AppOrderDetails from '@/js/vue/checkout/order-details.vue';
import ProductCost from '@/js/vue/checkout/product-cost.vue';
import ProductService from '@/js/vue/checkout/product-service.vue';
import { useCartTotal } from '@/js/vue/composable/cart-total.ts';
import { useMapgl } from '@/js/vue/composable/mapgl.ts';
import { useTotals } from '@/js/vue/composable/totals.ts';
import DoubleGisMap from '@/js/vue/map/double-gis-map.vue';
import { useDebounceFn } from '@vueuse/core';
import { computed, ref, shallowRef, watch } from 'vue';

const props = defineProps<{
  deliveryPricing: DeliveryPricing
  liftPricing: LiftPricing
  client: Client | null
  cart: Cart | null
  pickupPrice: number
}>();

const classCheckout = classname('checkout');

const { products, totals, setCartTotal } = useCartTotal();
watch(() => props.cart, (data) => {
  if (data) {
    setCartTotal(data);
  }
}, { immediate: true });

// Order
const orderData = ref<OrderRequest>({
  name: '',
  email: '',
  phone: '',

  legal_type: 'individual',
  legal_data: {
    inn: null,
  },

  delivery_type: null,
  delivery_data: {
    address: '',
    distance: 0,
    price: 0,
    response: [],
  },

  lift_data: {
    lift_level: 0,
    lift_price: 0,
  },

  payment_method: 'transfer',
  payment_data: {
    coupon: null,
    bonus: null,
  },

  additional_data: {
    call_time: '',
  },

  services: {
    projecting: false,
    installment_items: [],
  },
});

watch(() => props.client, (user) => {
  if (user) {
    orderData.value.name = `${user.firstName || ''}`;
    orderData.value.email = user.email;
    orderData.value.phone = user.phone;
  }
}, {
  immediate: true,
});
// errors
const errors = ref<string[]>([]);

function clearErrors() {
  errors.value = [];
}

watch(orderData, clearErrors, { deep: true });

// Payments
const paymentMethods = computed(() => getPaymentMethods());

// Delivery
const deliveryType = computed(() => orderData.value.delivery_type);
const deliveryCity = ref('');
const suggestionCity = ref<Suggestion>();
const suggestionAddress = ref<Suggestion>();
const isMoscowRegionDelivery = computed(() => suggestionCity.value?.data.region === MOSCOW_REGION || suggestionCity.value?.data.region === MOSKOVSKAYA_REGION);
const isMoscowDeliveryType = computed(() => orderData.value.delivery_type === 'self');
const deliveryMethods = computed(() => getDeliveryMethods(isMoscowRegionDelivery.value));
const deliveryTimes = computed(() => getDeliveryTimes());
const callTimes = computed(() => getCallTimes());
const floors = computed(() => getFloors());
const deliveryAddress = ref('');
const deliveryInfo = ref<DeliveryInfo>({
  flat: null,
  entrance: null,
  comment: null,
  date: '',
  time: '16:00 - 18:00',
  isRiseFloor: false,
  isNotElevator: false,
  raiseOnFloor: null,
  isTakesAnotherPerson: false,
  recipientPhone: '',
  recipientFullName: '',
  isQuestions: false,
});
const isCourierComment = ref(false);
const dadataParamsStreet = computed((): DadataParams => ({
  locations: [{ city_fias_id: suggestionCity.value?.data.city_fias_id }],
  from_bound: { value: 'street' },
  to_bound: { value: 'house' },
}));

watch(deliveryCity, () => {
  deliveryAddress.value = '';
});
const fullAddress = computed(() => `${deliveryCity.value} ${deliveryAddress.value}`);
watch(fullAddress, (address) => {
  orderData.value.delivery_data.address = address;
});

function sliceString(string: string) {
  if (!deliveryCity.value || !string) {
    return null;
  }
  return string.startsWith(deliveryCity.value)
    ? string.substring(deliveryCity.value.length + 2)
    : string;
}

function updateSuggestionCity(suggestions: Suggestion[]) {
  suggestionCity.value = suggestions.find(
    item => item.value === deliveryCity.value,
  );
}

function updateSuggestionAddress(suggestions: Suggestion[]) {
  suggestionAddress.value = suggestions.find(
    item => sliceString(item.value) === deliveryAddress.value,
  );
}

function updateSuggestionData(suggestion: Suggestion) {
  suggestionCity.value = suggestion;
  deliveryCity.value = suggestToCity(suggestion);
  suggestionAddress.value = suggestion;
  deliveryAddress.value = sliceString(suggestion.value) ?? '';
}

const isLoading = ref(false);
const isOrderAdded = ref(false);

async function addOrder() {
  try {
    isLoading.value = true;
    const result = await createOrder(cleanOrderFields(orderData.value));
    isOrderAdded.value = result.data?.success ?? false;
    if (result.data?.success && result.data.payment_url) {
      window.location.href = result.data.payment_url;
    }
    else {
      showErrorNotify({ text: ERROR_REQUEST_TEXT });
    }
  }
  catch (e) {
    console.log(e);
    showErrorNotify({ text: ERROR_REQUEST_TEXT });
  }
  finally {
    isLoading.value = false;
  }
}

// Installment
const isInstallationNeeded = ref(false);
const installmentProducts = computed(
  () =>
    products.value.filter(
      product => product.installmentPrice && product.installmentPrice > 0,
    ),
);
const installationProductIds = ref<number[]>([]);
watch(isInstallationNeeded, (needed) => {
  if (!needed) {
    installationProductIds.value = [];
  }
});
watch(installationProductIds, (ids) => {
  if (orderData.value.services) {
    orderData.value.services.installment_items = installmentProducts.value
      .filter(product => ids.includes(product.productId))
      .map(item => ({
        product_id: item.productId,
        price: item.installmentPrice ?? 0,
      }));
  }
});

// Map
const { getCarRoute, createLine, createLineByRoute } = useMapgl();
const mapInstance = shallowRef<mapgl.Map>();
const point = ref<number[]>();
const deliveryMarker = shallowRef<mapgl.Marker>();
const isInsideDelivery = ref(false);
const isOutsideDelivery = ref(false);
const mkadPoliline = ref<mapgl.Polyline>();
const carRouteLine = ref<mapgl.Polyline>();
const carRoute = ref<Route>();
const carRouteDistance = computed(() => carRoute.value?.total_distance ?? 0);
watch(carRouteDistance, (distance) => {
  orderData.value.delivery_data.distance = distance;
});
function clearCarRouteLine() {
  carRouteLine.value?.destroy();
}

const debouncedCarRoute = useDebounceFn((start, end) => getCarRoute(start, end), 1000);
async function getRouteToMkad(coors: number[]) {
  try {
    const startPoint = findClosestPointOnMkadPolygon(coors);
    const response = await debouncedCarRoute({ lon: startPoint[0], lat: startPoint[1] }, { lon: coors[0], lat: coors[1] });
    if (response.data?.result) {
      return response.data.result[0];
    }
    return null;
  }
  catch (e) {
    console.warn(e);
  }
}

async function showCarRouteLine(coors: number[]) {
  const response = await getRouteToMkad(coors);
  if (response) {
    carRoute.value = response;
    carRouteLine.value = createLineByRoute(mapInstance.value, response);
  }
}

watch(suggestionAddress, (suggestion) => {
  if (suggestion) {
    const coors = [Number(suggestion.data.geo_lon), Number(suggestion.data.geo_lat)];
    deliveryMarker.value.setCoordinates(coors);
    if (isOutsideDelivery.value) {
      getRouteToMkad(coors);
    }
  }
});
watch(isOutsideDelivery, (showMkadLine) => {
  if (showMkadLine) {
    const coors = convertCoordinates(mkad.coordinates);
    mkadPoliline.value = createLine(mapInstance.value, coors[0]);
  }
  else {
    mkadPoliline.value?.destroy();
  }
});
watch(mapInstance, (map) => {
  if (map) {
    map.on('click', async (event: mapgl.MapPointerEvent) => {
      const coors = event.lngLat;
      deliveryMarker.value?.setCoordinates(coors);
      isInsideDelivery.value = isPointInMkadPolygon(coors);
      isOutsideDelivery.value = isPointInMosoblPolygon(coors);
      clearCarRouteLine();
      if (isOutsideDelivery.value) {
        await showCarRouteLine(coors);
      }
      const { data } = await getGeocoding(coors[0], coors[1]);
      if (data && data.suggestions.length > 0) {
        updateSuggestionData(data.suggestions[0]);
      }
    });
  }
});
watch(suggestionCity, (city) => {
  if (city) {
    point.value = [Number(city.data.geo_lon), Number(city.data.geo_lat)];
  }
});

// Totals
const installmentPrice = computed(() => orderData.value.services?.installment_items.reduce((acc, curr) => acc + curr.price, 0) ?? 0);
const { totalQuantity, finalPrice, orderTotals, deliveryTotals } = useTotals({
  deliveryPricing: props.deliveryPricing,
  liftPricing: props.liftPricing,
  products,
  cartTotals: totals,
  installmentPrice,
  distance: carRouteDistance,
  isOutsideDelivery,
  isInsideDelivery,
  deliveryInfo,
  pickupPrice: props.pickupPrice,
  deliveryType,
});
watch(deliveryTotals, (delivery) => {
  if (delivery.length > 0) {
    orderData.value.delivery_data.price = Number(delivery[0].value);
  }
});
</script>

<template>
  <form :class="classCheckout()" @submit.prevent="addOrder">
    <div :class="classCheckout('col')">
      <!--      ваши данные -->
      <AppOrderDetails>
        <template #title>
          {{ getLocaleValue('account_profile') }}
        </template>
        <template #content>
          <div :class="classCheckout('your-data-inputs')">
            <BaseInputRadio
              v-model="orderData.legal_type"
              size="sm"
              value="individual"
              name="person"
              required
            >
              {{ getLocaleValue('individual') }}
            </BaseInputRadio>
            <BaseInputRadio
              v-model="orderData.legal_type"
              size="sm"
              value="organization"
              name="person"
              required
            >
              {{ getLocaleValue('legal_entity') }}
            </BaseInputRadio>
          </div>
          <div :class="classCheckout('phone-container', { error: errors.includes('phone') })">
            <BaseInputTel
              v-model="orderData.phone"
              :placeholder="getPhonePlaceholder()"
              required
            />
            <span v-html="getLocaleValue('enter_your_phone_number')" />
          </div>
          <BaseInput v-model="orderData.name" required :placeholder="getLocaleValue('full_name')" />
          <BaseInput
            v-model="orderData.email"
            type="email"
            required
            :placeholder="getLocaleValue('e_mail')"
          />
          <div
            v-if="orderData.legal_type === 'organization'"
            :class="classCheckout('company')"
          >
            <div :class="classCheckout('company-title')">
              {{ getLocaleValue('organization_details') }}
            </div>
            <BaseInput
              v-if="orderData.legal_data"
              v-model="orderData.legal_data.inn"
              type="number"
              :placeholder="getLocaleValue('inn_organization')"
              required
            />
          </div>
        </template>
      </AppOrderDetails>
      <AppOrderDetails v-if="installmentProducts.length > 0" size="large">
        <template #title>
          {{ getLocaleValue('additional_services') }}
        </template>
        <template #content>
          <AppAdditionalService v-if="orderData.services" v-model="orderData.services.projecting">
            <template #icon>
              <BaseIcon icon="ruler" type="main" :width="40" :height="40" />
            </template>
            <template #title>
              {{ getLocaleValue('planning_title') }}
            </template>
            <template #desc>
              {{ getLocaleValue('we_will_make_a_project') }}
            </template>
          </AppAdditionalService>
          <AppAdditionalService v-model="isInstallationNeeded">
            <template #icon>
              <BaseIcon icon="tools" type="main" :width="40" :height="40" />
            </template>
            <template #title>
              {{ getLocaleValue('installation') }}
            </template>
            <template #desc>
              {{ getLocaleValue('we_will_carry_out_assembly') }}
            </template>
          </AppAdditionalService>
          <div v-if="isInstallationNeeded">
            <ProductService
              v-for="product in installmentProducts"
              :key="product.id"
              v-model:product-ids="installationProductIds"
              :product-id="product.productId"
              :image="product.productImage.url ?? ''"
              :name="product.productName"
              :price="formatPrice(product.installmentPrice ?? 0)"
              url="/montazh"
            />
          </div>
          <AppHint :class="classCheckout('hint')">
            {{ getLocaleValue('the_manager_will_contact_you') }}
          </AppHint>
        </template>
      </AppOrderDetails>
      <!--      способ получения -->
      <AppOrderDetails>
        <template #title>
          {{ getLocaleValue('method_of_obtaining') }}
        </template>
        <template #content>
          <AddressSuggestions
            v-model="deliveryCity"
            :params="getDadataCityParams()"
            :placeholder="getLocaleValue('enter_locality')"
            required
            @update:suggestions="updateSuggestionCity"
          />
          <div v-if="suggestionCity">
            <BaseInputRadio
              v-for="deliveryMethod in deliveryMethods"
              :key="deliveryMethod.value"
              v-model="orderData.delivery_type"
              :value="deliveryMethod.value"
              name="delivery"
              required
            >
              {{ deliveryMethod.label }}
            </BaseInputRadio>
          </div>
        </template>
      </AppOrderDetails>
      <!--      адрес доставки -->
      <AppOrderDetails v-if="isMoscowDeliveryType">
        <template #title>
          {{ getLocaleValue('delivery_address') }}
        </template>
        <template #map>
          <div v-if="errors?.includes('map')">
            {{ getLocaleValue('map_error') }}
          </div>
          <DoubleGisMap
            v-if="point" v-model:map="mapInstance" v-model:marker="deliveryMarker"
            :center="point"
          />
        </template>
        <template #content>
          <AddressSuggestions
            v-model="deliveryAddress"
            :params="dadataParamsStreet"
            :callback="sliceString"
            :placeholder="getLocaleValue('enter_your_shipping_address')"
            required
            @update:suggestions="updateSuggestionAddress"
          />
          <div :class="classCheckout('columns-2')">
            <BaseInput
              v-model="deliveryInfo.flat"
              type="number"
              min="0"
              :placeholder="getLocaleValue('apt_office')"
            />
            <BaseInput
              v-model="deliveryInfo.entrance"
              type="number"
              min="0"
              :placeholder="getLocaleValue('entrance')"
            />
          </div>
          <BaseTextarea
            v-if="isCourierComment"
            v-model="deliveryInfo.comment"
            :placeholder="getLocaleValue('comment_for_the_courier')"
          />
          <div>
            <button
              v-if="!isCourierComment"
              :class="classCheckout('button')"
              @click="isCourierComment = true"
            >
              <BaseIcon type="main" icon="plus" :width="19" :height="18" />
              {{ getLocaleValue('add_a_comment_for_the_courier') }}
            </button>
          </div>
        </template>
      </AppOrderDetails>
      <!--      способ оплаты -->
      <AppOrderDetails>
        <template #title>
          {{ getLocaleValue('payment_method') }}
        </template>
        <template #content>
          <BaseInputRadio
            v-for="(item) in paymentMethods"
            :key="item.value"
            v-model="orderData.payment_method"
            name="payment"
            :value="item.value"
            required
          >
            {{ item.label }}
          </BaseInputRadio>
        </template>
      </AppOrderDetails>
      <!--      условия доставки -->
      <AppOrderDetails v-if="isMoscowDeliveryType">
        <template #title>
          {{ getLocaleValue('conditions_for_the_delivery') }}
        </template>
        <template #content>
          <div :class="classCheckout('date-delivery')">
            <BaseInputDate v-model="deliveryInfo.date">
              {{ getLocaleValue('delivery_date') }}
            </BaseInputDate>
            <BaseSelect
              v-model="deliveryInfo.time"
              :options="deliveryTimes"
              required
            >
              {{ getLocaleValue('delivery_interval') }}
            </BaseSelect>
          </div>
          <AppHint type="secondary">
            {{ getLocaleValue('courier_will_contact') }}
          </AppHint>
          <div :class="classCheckout('col-grid')">
            <AppStatus v-model="deliveryInfo.isRiseFloor">
              {{ getLocaleValue('a_lift_to_the_floor') }}
            </AppStatus>
            <div v-if="deliveryInfo.isRiseFloor" :class="classCheckout('col-grid')">
              <BaseCheckbox v-model="deliveryInfo.isNotElevator">
                {{ getLocaleValue('no_elevator') }}
              </BaseCheckbox>
              <BaseSelect
                v-if="deliveryInfo.isNotElevator"
                v-model="deliveryInfo.raiseOnFloor"
                :placeholder="getLocaleValue('select_floor')"
                :options="floors"
              >
                {{ getLocaleValue('lifting_it_up') }}
              </BaseSelect>
            </div>
          </div>

          <div :class="classCheckout('col-grid')">
            <AppStatus v-model="deliveryInfo.isTakesAnotherPerson">
              {{ getLocaleValue('another_person_will_receive_the_order') }}
            </AppStatus>
            <div
              v-if="deliveryInfo.isTakesAnotherPerson"
              :class="classCheckout('col-grid')"
            >
              <BaseInputTel
                v-model="deliveryInfo.recipientPhone"
                required
                :placeholder="getLocaleValue('recipients_phone_number')"
              />
              <BaseInput
                v-model="deliveryInfo.recipientFullName"
                :placeholder="getLocaleValue('recipients_full_name')"
              />
            </div>
          </div>
        </template>
      </AppOrderDetails>
      <!--      дополнительно, скрыто -->
      <!--      <AppOrderDetails v-if="false"> -->
      <!--        <template #title> -->
      <!--          Дополнительно -->
      <!--        </template> -->
      <!--        <template #content> -->
      <!--          <BaseTextarea -->
      <!--            v-model="userData.orderComments" -->
      <!--            placeholder="Комментарий к заказу" -->
      <!--          /> -->
      <!--          <AppStatus v-model="userData.isQuestions"> -->
      <!--            У меня остались вопросы по заказу -->
      <!--          </AppStatus> -->
      <!--          <AppStatus v-model="userData.isTakesAnotherPerson"> -->
      <!--            Заказ будет получать другой человек -->
      <!--          </AppStatus> -->
      <!--        </template> -->
      <!--      </AppOrderDetails> -->
      <!-- у меня остались вопросы -->
      <div :class="classCheckout('questions')">
        <AppOrderDetails :is-heading="false">
          <template #content>
            <AppCallManager v-model="deliveryInfo.isQuestions" />
            <BaseSelect
              v-if="deliveryInfo.isQuestions && orderData.additional_data"
              v-model="orderData.additional_data.call_time"
              :options="callTimes"
              :placeholder="getLocaleValue('select_time')"
            >
              {{ getLocaleValue('when_should_i_call_you') }}
            </BaseSelect>
          </template>
        </AppOrderDetails>
      </div>
    </div>
    <div :class="classCheckout('col')">
      <div :class="classCheckout('product-cost-wrapper')">
        <div :class="classCheckout('product-cost')">
          <ProductCost
            :class="classCheckout('cost')"
            :totals="orderTotals"
            :final-price
          />
          <div :class="classCheckout('button-wrapper')">
            <div
              v-show="!isOrderAdded"
              :class="classCheckout('button-container')"
            >
              <div :class="classCheckout('quantity-text')">
                {{ totalQuantity }}
              </div>
              <div :class="classCheckout('total-price')">
                {{ finalPrice }}
              </div>
              <BaseButton :disabled="isLoading" type="submit" theme="primary" :class="classCheckout('submit')">
                {{ getLocaleValue('place_an_order') }}
              </BaseButton>
            </div>
            <AppLoader v-show="isOrderAdded" :class="classCheckout('loader')" />
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<style lang="scss">
.checkout {
  display: grid;
  grid-gap: 140px;
  grid-template-columns: 4fr 3fr;

  &__button {
    display: flex;
    grid-gap: 12px;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
    font-family: inherit;
    background-color: transparent;
    border: none;
    outline: none;
    cursor: pointer;

    &_type {
      &_map {
        color: #000;
        font-size: 18px;
      }
    }
  }

  &__col {
    display: grid;
    grid-gap: 44px;
  }

  &__col-grid {
    display: grid;
    grid-gap: 26px;
  }

  &__product-cost {
    position: sticky;
    top: 20px;
    margin-top: 20px;
  }

  &__your-data-inner {
    display: grid;
    grid-gap: 26px;
  }

  &__your-data-inputs {
    display: grid;
    grid-gap: 10px;
    grid-template-columns: repeat(2, auto);
    max-width: 400px;
  }

  &__date-delivery {
    display: grid;
    grid-gap: 19px;
    grid-template-columns: repeat(2, 1fr);
  }

  &__columns-2 {
    display: grid;
    grid-gap: 18px;
    grid-template-columns: repeat(2, 1fr);
  }

  &__company {
    display: grid;
    grid-gap: 26px;
    padding-top: 26px;
    border-top: 1px solid #979797;
  }

  &__promo-code {
    display: grid;
    grid-gap: 20px;
    grid-template-columns: 1fr 1fr;
    margin: 0;
  }

  &__company-title {
    font-size: 24px;
  }

  &__company-info {
    color: #999;
    font-size: 20px;
  }

  &__button-wrapper {
    display: grid;
    grid-gap: 20px;
    margin-top: 30px;
  }

  &__lift-price {
    color: #c4c4c4;
  }

  &__update-uds {
    width: 100%;
    margin-top: 36px;
  }

  &__quantity-text,
  &__total-price {
    display: none;
  }

  &__loader {
    margin-inline: auto;
  }

  &__phone-container {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    color: #2779cf;

    input {
      box-sizing: border-box;
      width: 100%;
      padding: 22px 14px;
      font-size: 20px;
      line-height: 1.15;
      border: 1px solid #2779cf;
      border-radius: 2px;
      outline: none;

      &::placeholder {
        color: var(--base-input-tel-color);
      }

      &:not(:read-only, .error) {
        &:hover,
        &:focus {
          border-color: #000;
        }
      }

      &:not(:read-only, .error) {
        &:hover,
        &:focus {
          border-color: #2779cf;
        }
      }
    }

    &.error {
      input {
        border-color: red;
      }
    }
  }

  @media screen and (max-width: 1200px) {
    grid-gap: 50px;
  }

  @media screen and (max-width: 991px) {
    grid-template-columns: 1fr;

    &__company {
      gap: 16px;
      margin-top: 16px;
      padding-top: 0;
      border-top: none;
    }

    &__company-title {
      font-weight: 500;
      font-size: 16px;
    }

    &__company-info {
      color: #b2b2b2;
      font-size: 16px;
    }

    //    TODO: mm-page mm-slideout ломают стили
    &__button-wrapper {
      position: fixed;
      z-index: 11;
      right: 0;
      bottom: env(safe-area-inset-bottom, 0);
      left: 0;
      margin-top: 0;
      padding: 8px;
      background-color: #ecf1f7;
    }

    &__button-container {
      display: grid;
      grid-template-areas: 'text price' 'button button';
      gap: 8px;
      align-items: center;
    }

    &__quantity-text {
      grid-area: text;
      color: #898989;
      font-size: 14px;
      font-weight: 400;
    }

    &__submit {
      grid-area: button;
    }

    &__total-price {
      grid-area: price;
      font-size: 20px;
      font-weight: 700;
      text-align: right;
    }

    &__quantity-text,
    &__total-price {
      display: block;
    }

    &__product-cost {
      position: unset;
    }

    &__uds,
    &__promo-code,
    &__cost,
    &__questions {
      display: none;
    }

    &__phone-container {
      input {
        padding: 14px 24px;
        font-size: 16px;
      }

      &__info {
        display: none;
      }
    }
  }

  @media screen and (max-width: 768px) {
    &__col {
      grid-gap: 20px;
    }

    &__promo-code {
      grid-gap: 11px;
    }

    &__update-uds {
      margin-top: 32px;
    }
  }

  @media screen and (max-width: 500px) {
    &__date-delivery {
      grid-gap: 16px;
      grid-template-columns: repeat(1, 1fr);
    }
  }

  @media screen and (max-width: 426px) {
    &__promo-code {
      grid-template-columns: 1fr;
    }
  }
}
</style>
